import React, { useState, useEffect } from "react";
import { User } from "lucide-react";

export default function EmployeeAvatarCell({
  employee,
  profilePic,
  language,
  theme,
  getEmployeeName, // Optional: function to resolve the employee's name
  onClick, // Optional: makes the avatar/name clickable (e.g. open view modal)
}) {
  const getScreenSize = () => {
    if (typeof window === "undefined") return "md";
    const width = window.innerWidth;
    if (width < 640) return "xs";
    if (width < 768) return "sm";
    if (width < 1024) return "md";
    if (width < 1280) return "lg";
    if (width < 1536) return "xl";
    return "xxl";
  };

  const [screenSize, setScreenSize] = useState(getScreenSize());

  useEffect(() => {
    const handleResize = () => setScreenSize(getScreenSize());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const style = {
    textPrimary: theme === "light" ? "#1a1a1a" : "#ffffff",
    textMuted: theme === "light" ? "rgba(26, 26, 26, 0.6)" : "rgba(255, 255, 255, 0.6)",
  };

  const genderAvatarBg = (gender) => {
    if (gender === "Male") return "linear-gradient(135deg, #60a5fa 0%, #2563eb 100%)";
    if (gender === "Female") return "linear-gradient(135deg, #f472b6 0%, #db2777 100%)";
    return "linear-gradient(135deg, #94a3b8 0%, #64748b 100%)";
  };

  const gender = employee.gender || employee.fullEmployeeData?.gender;

  const nameToDisplay = getEmployeeName 
    ? getEmployeeName(employee) 
    : (language === "ar" ? (employee.arabicName || employee.name || "N/A") : (employee.name || "N/A"));

  const emailToDisplay = employee.email || "";

  const avatarStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: screenSize === "xs" ? "32px" : "40px",
    height: screenSize === "xs" ? "32px" : "40px",
    borderRadius: "50%",
    background: genderAvatarBg(gender),
    color: "#fff",
    flexShrink: 0,
  };

  const employeeNameStyle = {
    fontWeight: "600",
    color: style.textPrimary,
    display: "block",
    fontSize: screenSize === "xs" ? "13px" : "14px",
  };

  const employeeEmailStyle = {
    fontSize: "12px",
    color: style.textMuted,
    display: "block",
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        cursor: onClick ? "pointer" : "default",
      }}
      onClick={onClick}
    >
      <div style={{ position: "relative", ...avatarStyle, marginLeft: 0 }}>
        <User size={screenSize === "xs" ? 16 : 20} strokeWidth={2.25} />
        {profilePic && (
          <img
            src={profilePic}
            alt=""
            loading="lazy"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              borderRadius: "50%",
              objectFit: "cover",
            }}
          />
        )}
      </div>
      <div
        style={{
          flex: 1,
          textAlign: language === "ar" ? "right" : "left",
          marginLeft: language === "ar" ? "0px" : "10px",
          marginRight: language === "ar" ? "10px" : "0px",
          direction: language === "ar" ? "rtl" : "ltr",
        }}
      >
        <div style={{
          ...employeeNameStyle,
          textAlign: language === "ar" ? "right" : "left",
        }}>
          {nameToDisplay}
        </div>
        <div style={{
          ...employeeEmailStyle,
          textAlign: language === "ar" ? "right" : "left",
        }}>
          {emailToDisplay}
        </div>
      </div>
    </div>
  );
}
